/** @jsx jsx */
import { jsx } from "@emotion/core";
import * as React from "react";
import { SelfieVideoprops } from "./interfaces";
import { selfieVideoStyle } from "./styles";
import { responsiveStyles } from "../../commons/styles";
import { UploadComp } from "../../molecules/Upload";
import {
  UploadTypes,
  ButtonClickType,
  inputFieldOptionalLabel,
  getSelfieVideoDescription,
  inputFieldMandatoryLabel,
} from "../../constants/commons";
import { RegistrationFooterButton } from "../../components/RegistrationFooterButton";
import { RegistrationLayout } from "../../components/RegistrationLayout";
import {
  availableUploadType,
  checkIfUploadIsPresentOfType,
  displayConfirmationWhenWindowCloses,
  getFieldConfigStatus,
  getImgURLFromBufferData,
  isFieldOptional,
  showDisplayHeadingAndContentFromLoadingStatus,
} from "../../utils/helpers";
import useSTR from "react-str";
import { deleteData, getData, postData } from "../../utils/fetchers";
import { URL } from "../../API/urlEndpoints";
import { ShowMessage } from "../../components/ShowMessage";
import { toast } from "../../utils/toast";
import { useUserContext } from "../../hooks/cookieHooks";
import * as _ from "lodash";
import { ErrorComponent } from "../../molecules/ErrorComponent";
import {
  FieldRenderState,
  Profession,
  RegisterationFields,
} from "../../commons/interfaces";

export const SelfieVideoPage: React.FunctionComponent<SelfieVideoprops> = (
  props
) => {
  const { pageNavigationDetails, SuccessFullSaveObj, isLastStep } = props;
  const { nextScreen } = pageNavigationDetails;

  //State to maintain the uploaded docs/images
  const [successfullUploadedDocs, setSuccessfullUploadedDocs] = React.useState<
    any[]
  >([]);
  // State checking whether user can be navigated to the next page or not
  const [
    isNextPageNavAllowed,
    setIsNextPageNavAllowed,
  ] = React.useState<boolean>(false);
  // State to maintain whether the graduation degree is uploaded or not
  const [
    isSelfieVideoUploaded,
    setIsSelfieVideoUploaded,
  ] = React.useState<boolean>();
  // Calling the SSO Hook to retrieve user information of the logged in user
  const user = useUserContext();

  // [POST] For Saving data on page.
  const [
    ,
    {
      result: postSelfieVideo,
      revalidator: refetchSelfieVideo,
      loading: loadingSelfieVideo,
      error: errorSelfieVideo,
    },
  ] = useSTR(postData);

  // [GET] For Saving data on page.
  const [
    ,
    {
      result: getSelfieVideoData,
      revalidator: refetchGETSelfieVideo,
      loading: loadingGETSelfieVideo,
      error: errorGETSelfieVideo,
    },
  ] = useSTR(getData);

  // [DELETE] For removing the uploaded Image
  const [
    ,
    {
      result: removeUploadedAsset,
      revalidator: refetchDeleteUploadedAsset,
      loading: loadingRemovedAsset,
      error: errorRemovedAsset,
    },
  ] = useSTR(deleteData);

  React.useEffect(() => {
    refetchGETSelfieVideo(`${URL.getSelfieVideoPage(_.get(user, "empId"))}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Function which is called whewn the browser closes
  displayConfirmationWhenWindowCloses();
  const { onBackButtonClick, onNextClick } = props;

  // Hook which is called when an upload is successfully added/removed.
  // Updating the state here so that the submit button could be enabled/disabled based on the Profile Picture Upload
  React.useEffect(() => {
    setIsSelfieVideoUploaded(
      checkIfUploadIsPresentOfType(
        UploadTypes.SELFIE_VIDEO,
        successfullUploadedDocs
      )[0]
    );
  }, [successfullUploadedDocs]);

  // Function called whenever the user successfully uploads an asset
  const onSuccessfullFileUpload = (onSuccessFileUploadObj: any) => {
    setSuccessfullUploadedDocs((oldUploadedAsset) => [
      ...oldUploadedAsset,
      onSuccessFileUploadObj,
    ]);
  };

  // Function which is called when the ProfilePicture is removed
  const removeSelectedAsset = (removedAsset: any) => {
    const { filename, id } = removedAsset;
    refetchDeleteUploadedAsset(
      `${URL.removeUploadedAsset(_.get(user, "empId"), id, filename)}`
    );
    // Filtering and removing the DELETED Asset from the ASSET's Collection
    setSuccessfullUploadedDocs(
      successfullUploadedDocs.filter(
        (uploadedDocs) => uploadedDocs !== removedAsset
      )
    );
  };

  // Function which is executed after successfully removing the uploaded Asset
  React.useEffect(() => {
    if (removeUploadedAsset && removeUploadedAsset.success) {
      toast.success(removeUploadedAsset.message);
    }
  }, [removeUploadedAsset]);

  // Variable which shows the loading state
  const isScreenLoading = React.useMemo(() => {
    return loadingRemovedAsset || loadingSelfieVideo || loadingGETSelfieVideo;
  }, [loadingRemovedAsset, loadingSelfieVideo, loadingGETSelfieVideo]);

  const onPositiveActionClick = (buttonClickType: ButtonClickType) => {
    switch (buttonClickType) {
      case ButtonClickType.NEXT:
        let paramsObj = {
          customHeader: { change_state: true },
        };
        refetchSelfieVideo(
          `${URL.postSelfieVideoPage(_.get(user, "empId"))}`,
          paramsObj
        );
        setIsNextPageNavAllowed(true);
        break;

      case ButtonClickType.SAVE_AND_EXIT:
        refetchSelfieVideo(
          `${URL.postSelfieVideoPage(_.get(user, "empId"))}`,
          {}
        );
        setIsNextPageNavAllowed(false);
        break;

      default:
        break;
    }
  };

  // Showing the success message in a toast component / Fetching data From Server
  React.useEffect(() => {
    if (postSelfieVideo) {
      postSelfieVideo?.message && toast.success(postSelfieVideo.message);
      if (isNextPageNavAllowed) {
        onNextClick(nextScreen);
      } else {
        SuccessFullSaveObj(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postSelfieVideo, isNextPageNavAllowed]);

  // Hook which is called whenever there is an error in either GET/POST calls
  const showErrorComponent = React.useMemo(() => {
    return errorRemovedAsset || errorGETSelfieVideo || errorSelfieVideo;
  }, [errorGETSelfieVideo, errorRemovedAsset, errorSelfieVideo]);

  // For showing/hiding the SELFIE_VIDEO field
  const showSelfieVideoField = React.useMemo(() => {
    return (
      getSelfieVideoData &&
      getSelfieVideoData.fieldsconfig &&
      _.keys(getSelfieVideoData.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getSelfieVideoData.fieldsconfig,
        RegisterationFields.SELFIE_VIDEO_UPLOAD
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getSelfieVideoData]);

  // For showing the label OPTIONAL for the field [SELFIE_VIDEO]
  const showSelfieVideoOptionalLabel = React.useMemo(() => {
    return (
      getSelfieVideoData &&
      getSelfieVideoData.fieldsconfig &&
      _.keys(getSelfieVideoData.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getSelfieVideoData.fieldsconfig,
          RegisterationFields.SELFIE_VIDEO_UPLOAD
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getSelfieVideoData]);

  // To fetch the data from the GET API
  React.useEffect(() => {
    if (getSelfieVideoData) {
      const { selfieVideos } = getSelfieVideoData;

      // To get the selected Documents from the server on Page load
      let documentsFromServer = getImgURLFromBufferData(selfieVideos);
      documentsFromServer && setSuccessfullUploadedDocs(documentsFromServer);
    }
  }, [getSelfieVideoData]);

  // Function which allows the user to go to the next screen
  const isSubmitAllowed = React.useMemo(() => {
    return !(showSelfieVideoField ? isSelfieVideoUploaded : true);
  }, [showSelfieVideoField, isSelfieVideoUploaded]);

  const getExplanation = () => {
    const content = getSelfieVideoDescription(
      user.memberships[0].context as Profession
    );
    if (content) {
      return content.map((item, key) => {
        return (
          <React.Fragment key={key}>
            <h6>{item.heading}</h6>
            <ul>
              {item.bullets.map((eachPoint, pointKey) => {
                return <li key={pointKey}>{eachPoint}</li>;
              })}
            </ul>
            <div>{item.extraInfo}</div>
          </React.Fragment>
        );
      });
    } else return <React.Fragment />;
  };

  // Getting hold of the different upload types for selfie video based on type of profession
  const allowedFileFOrmatsForUpload = React.useMemo(() => {
    return (
      _.get(user, "memberships", "") &&
      _.keys(_.get(user, "memberships", "")).length > 0 &&
      _.get(user, "memberships", "")[0].context &&
      availableUploadType(_.get(user, "memberships", "")[0].context)
    );
  }, [user]);

  return (
    <span css={[responsiveStyles, selfieVideoStyle]}>
      <ShowMessage
        visible={isScreenLoading}
        loading={isScreenLoading}
        heading={
          showDisplayHeadingAndContentFromLoadingStatus({
            postLoginScreen: loadingRemovedAsset || loadingSelfieVideo,
          })?.loadingHeading
        }
        description={
          showDisplayHeadingAndContentFromLoadingStatus({
            postLoginScreen: loadingRemovedAsset || loadingSelfieVideo,
          })?.loadingDescription
        }
      />
      <RegistrationLayout>
        {showSelfieVideoField && (
          <React.Fragment>
            <div className="d-flex align-items-center">
              <h2>Selfie Video</h2>
              <span className="small-text ml-2">
                {`${
                  showSelfieVideoOptionalLabel
                    ? showSelfieVideoOptionalLabel
                    : inputFieldMandatoryLabel
                }
              `}
              </span>
            </div>

            <div className="my-3">
              <div className="message-style">{getExplanation()}</div>
            </div>
            <div className="col-12 pl-0">
              <UploadComp
                uploadType={UploadTypes.SELFIE_VIDEO}
                onSuccess={onSuccessfullFileUpload}
                allowedFilesType={allowedFileFOrmatsForUpload}
                uploadedFiles={successfullUploadedDocs}
                removeSelectedAsset={removeSelectedAsset}
              />
            </div>
          </React.Fragment>
        )}
        {showErrorComponent && <ErrorComponent errorObj={showErrorComponent} />}
      </RegistrationLayout>

      <RegistrationFooterButton
        disableNextButton={isSubmitAllowed}
        onPositiveActionClick={onPositiveActionClick}
        onBackButtonClick={onBackButtonClick}
        isLastStep={isLastStep}
      />
    </span>
  );
};
